import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AppearanceProvider, Toaster } from "@eduviaos/ui-kit";
import { getCurrentTenant } from "@eduviaos/shared";
import { AuthProvider } from "@/auth/AuthProvider";
import { TenantBrandingProvider } from "@/components/TenantBrandingProvider";
import { RequireAuth } from "@/auth/RequireAuth";
import { SUPPORT_SESSION_PATH } from "@/auth/supportSession";
import { SupportSessionGate } from "@/components/SupportSessionGate";
import { SupportSessionPage } from "@/pages/SupportSessionPage";
import { LoginPage } from "@/pages/LoginPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { AcceptInvitePage } from "@/pages/AcceptInvitePage";
import { AdmissionsPage } from "@/pages/AdmissionsPage";
import { IaAdmissionsEditorPage } from "@/pages/admissions/IaAdmissionsEditorPage";
import { SeatConfigPage } from "@/pages/seating/SeatConfigPage";
import { ZonesPage } from "@/pages/seating/ZonesPage";
import { SeatsPage } from "@/pages/seating/SeatsPage";
import { AllocationsPage } from "@/pages/seating/AllocationsPage";
import { BookingsPage } from "@/pages/seating/BookingsPage";
import { FloorPlanPage } from "@/pages/seating/FloorPlanPage";
import { SeatPlansPage } from "@/pages/seating/SeatPlansPage";
import { LiveFloorPage } from "@/pages/seating/LiveFloorPage";
import { AdmissionsCalendarPage } from "@/pages/AdmissionsCalendarPage";
import { AdmissionsWorkloadPage } from "@/pages/AdmissionsWorkloadPage";
import { AdmissionsTrashPage } from "@/pages/AdmissionsTrashPage";
import { EnquiryDetailPage } from "@/pages/EnquiryDetailPage";
import { FirstRunWelcomePage } from "@/pages/FirstRunWelcomePage";
import { FirstRunBrandingPage } from "@/pages/FirstRunBrandingPage";
import { FirstRunInviteStaffPage } from "@/pages/FirstRunInviteStaffPage";
import { FirstRunImportStudentsPage } from "@/pages/FirstRunImportStudentsPage";
import { FirstRunCommsPage } from "@/pages/FirstRunCommsPage";
import { FirstRunGoLivePage } from "@/pages/FirstRunGoLivePage";
import { ImportStudentsPage } from "@/pages/ImportStudentsPage";
import { StaffInviteAcceptPage } from "@/pages/StaffInviteAcceptPage";
import { ModuleComingSoonPage } from "@/pages/ModuleComingSoonPage";
import { PeoplePage } from "@/pages/people/PeoplePage";
import { TeamPage } from "@/pages/people/TeamPage";
import { AccountingPage } from "@/pages/accounting/AccountingPage";
import { AttendancePage } from "@/pages/attendance/AttendancePage";
import { TeamPayPage } from "@/pages/team-pay/TeamPayPage";
import { FeesPage } from "@/pages/fees/FeesPage";
import { CommunicationsPage } from "@/pages/communications/CommunicationsPage";
import { ReportsPage } from "@/pages/reports/ReportsPage";
import { AuditExportPage } from "@/pages/audit-export/AuditExportPage";
import { HelpdeskPage } from "@/pages/helpdesk/HelpdeskPage";
import { LearningPage } from "@/pages/learning/LearningPage";
import { DpdpConsentPage } from "@/pages/dpdp-consent/DpdpConsentPage";
import { TimetablePage } from "@/pages/TimetablePage";
import { TimetableDetailPage } from "@/pages/TimetableDetailPage";
import { TeacherSchedulePage } from "@/pages/TeacherSchedulePage";
import { HolidaysPage } from "@/pages/HolidaysPage";
import { CataloguePage } from "@/pages/catalogue/CataloguePage";
import { SubjectsCataloguePage } from "@/pages/catalogue/SubjectsCataloguePage";
import { SectionsCataloguePage } from "@/pages/catalogue/SectionsCataloguePage";
import { EligibilityMatrixPage } from "@/pages/catalogue/EligibilityMatrixPage";
import { AssessmentsPage } from "@/pages/assessments/AssessmentsPage";
import { AssessmentTopicsPage } from "@/pages/assessments/AssessmentTopicsPage";
import { AssessmentQuestionBankPage } from "@/pages/assessments/AssessmentQuestionBankPage";
import { AssessmentTemplatesPage } from "@/pages/assessments/AssessmentTemplatesPage";
import { AssessmentTemplateDetailPage } from "@/pages/assessments/AssessmentTemplateDetailPage";
import { FormsListPage } from "@/pages/forms/FormsListPage";
import { IaFormEditorPage } from "@/pages/forms/IaFormEditorPage";
import { FORM_EDITOR_ROUTE_PATTERN } from "@/pages/forms/formsRoutes";
import { SettingsPage } from "@/pages/SettingsPage";
import { SettingsBrandingPage } from "@/pages/SettingsBrandingPage";
import { SecurityPage } from "@/pages/SecurityPage";
import { DesignationsPage } from "@/pages/settings/DesignationsPage";
import { NotificationTriggersSettingsRoute } from "@/pages/settings/NotificationTriggersSettingsRoute";
import { AdmissionsLifecyclePage } from "@/pages/settings/AdmissionsLifecyclePage";
import { LearnerAppSettingsPage } from "@/pages/settings/LearnerAppSettingsPage";
import { LearnerFormSettingsPage } from "@/pages/settings/LearnerFormSettingsPage";
import { TeamFormSettingsPage } from "@/pages/settings/TeamFormSettingsPage";
import { LearningDefaultsSettingsPage } from "@/pages/settings/LearningDefaultsSettingsPage";
import { AppShell } from "@/components/AppShell";
import { PrerequisiteGate } from "@/components/PrerequisiteGate";
import { RouteModuleGate } from "@/components/RouteModuleGate";
import { RouteVerticalConceptGate } from "@/components/RouteVerticalConceptGate";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      // The cookie-refresh dance handles 401s transparently; let
      // TanStack Query treat data as fresh for 30s to avoid double-
      // fetches on tab refocus during dev.
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  // Phase H0 hotfix: resolve the tenant slug from the host name so
  // <TenantBrandingProvider> can fetch + apply the tenant's palette
  // and logo to the IA shell. The slug is stable for the lifetime of
  // a tab (tenants don't change subdomain mid-session) so we read it
  // ONCE at app boot. Falsy when running on the bare apex (dev) — the
  // provider becomes a no-op and the default palette renders.
  const tenantSlug = getCurrentTenant().slug ?? null;
  return (
    <AppearanceProvider defaultPalette="ocean-breeze">
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <TenantBrandingProvider slug={tenantSlug}>
              <SupportSessionGate>
                <Routes>
                  {/* Public onboarding redemption — no auth required.
                  MUST sit outside the RequireAuth wrapper so a freshly-
                  invited admin without any session can reach it. */}
                  <Route path="/onboarding/accept/:token" element={<AcceptInvitePage />} />
                  {/* Phase C1b — public staff-invite redemption. Same
                  constraint: pre-auth users land here. */}
                  <Route path="/staff-invite/:token" element={<StaffInviteAcceptPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  {/* Phase SC-22 — SA "Open centre admin" landing; public
                  because the one-time code in the fragment IS the auth. */}
                  <Route path={SUPPORT_SESSION_PATH} element={<SupportSessionPage />} />
                  {/* Phase C1a — first-run wizard sits OUTSIDE the AppShell
                  so the welcome step can claim the full viewport (no
                  sidebar / topbar). RequireAuth still wraps it because
                  only authenticated principals should reach the URL. */}
                  <Route
                    path="/first-run/welcome"
                    element={
                      <RequireAuth>
                        <FirstRunWelcomePage />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/first-run/branding"
                    element={
                      <RequireAuth>
                        <FirstRunBrandingPage />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/first-run/invite-staff"
                    element={
                      <RequireAuth>
                        <FirstRunInviteStaffPage />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/first-run/import-students"
                    element={
                      <RequireAuth>
                        <FirstRunImportStudentsPage />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/first-run/comms"
                    element={
                      <RequireAuth>
                        <FirstRunCommsPage />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/first-run/go-live"
                    element={
                      <RequireAuth>
                        <FirstRunGoLivePage />
                      </RequireAuth>
                    }
                  />
                  <Route
                    element={
                      <RequireAuth>
                        <AppShell />
                      </RequireAuth>
                    }
                  >
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/admissions" element={<AdmissionsPage />} />
                    {/* Phase D3/D4/D5 — admissions sub-pages. Routed BEFORE
                    `/:enquiryId` so the path-matcher resolves the static
                    segments first (the detail route uses any string,
                    so without ordering it would swallow these). */}
                    <Route path="/admissions/calendar" element={<AdmissionsCalendarPage />} />
                    <Route path="/admissions/workload" element={<AdmissionsWorkloadPage />} />
                    <Route path="/admissions/trash" element={<AdmissionsTrashPage />} />
                    {/* Phase F-finale-6 Slice 6b — tenant-self admission-funnel
                    editor. Static path, routed BEFORE `/:enquiryId`. */}
                    <Route path="/admissions/funnel" element={<IaAdmissionsEditorPage />} />
                    <Route path="/admissions/:enquiryId" element={<EnquiryDetailPage />} />
                    {/* Phase F-finale-7 Slice 7b.1 — tenant-self seat-assignment
                    config editor (study-centre). Nav entry is gated to
                    study_centre + manage_zones in AppShell; the page itself
                    renders a view-only notice without manage_zones. */}
                    <Route path="/seating/config" element={<SeatConfigPage />} />
                    {/* Phase F-finale-7 Slice 7b.4a — seat-management Zones
                    surface. Same study_centre + manage_zones gating as the
                    config editor; the page renders a view-only notice
                    without manage_zones. */}
                    <Route path="/seating/zones" element={<ZonesPage />} />
                    {/* Slice 7b.4b — Seats CRUD; same manage_zones gate + view-only
                    fallback as Zones (SeatsPage renders the notice internally). */}
                    <Route path="/seating/seats" element={<SeatsPage />} />
                    {/* Slice 7b.4c — fixed-mode permanent allocations; same
                    manage_zones gate + view-only fallback. The page surfaces a
                    fixed-mode notice when the tenant isn't in fixed mode. */}
                    <Route path="/seating/allocations" element={<AllocationsPage />} />
                    {/* Slice 7b.4d — dynamic/zone-only per-session booking admin
                    (check-in / cancel). Gated study_centre + manage_bookings in
                    AppShell; the page renders a view-only notice without
                    manage_bookings and a mode notice outside a booking mode. */}
                    <Route path="/seating/bookings" element={<BookingsPage />} />
                    {/* Phase SC-22c — floor plan editor, learner seat plans and
                    the live floor. Same nav gates as the seat cluster. */}
                    <Route path="/seating/floor-plan" element={<FloorPlanPage />} />
                    <Route path="/seating/plans" element={<SeatPlansPage />} />
                    <Route path="/seating/live" element={<LiveFloorPage />} />
                    {/* Phase D0c — `ModuleComingSoonPage` is the catch-all for
                    modules whose BE data plane hasn't shipped yet
                    (`empty_states === "not_implemented"`). The catalog
                    in `lib/moduleCatalog.ts` defines every route below
                    so nav clicks from the sidebar always land
                    SOMEWHERE — never on a 404. As later phases ship
                    the real surface for a module (D2 Fees, D3
                    Attendance, ...) we replace the matching line
                    here with a real page component. */}
                    {/* Phase J1 — `/people` is no longer a Coming-Soon stub.
                    Renders the 3-tab People shell (Students live, Staff
                    + Parents are slice J2/J3 placeholders). The dashboard
                    has been advertising the People module as "Live"
                    since Phase D0c; this route retires the drift between
                    that "Live" badge and the previous Coming-Soon page. */}
                    <Route path="/people" element={<PeoplePage />} />
                    <Route path="/team" element={<TeamPage />} />
                    {/* Phase H6d — standing-importer surface. Lives at
                    /imports/students (rather than /people/students/import)
                    so it can ship before the People module's data plane
                    arrives in Phase J. Permission-gated inside the page
                    on `import_students`; the BE additionally rejects
                    super_admin tokens to keep the SA out of tenant
                    data-plane writes. */}
                    <Route path="/imports/students" element={<ImportStudentsPage />} />
                    <Route
                      path="/fees"
                      element={
                        <RouteModuleGate module="fees">
                          <FeesPage />
                        </RouteModuleGate>
                      }
                    />
                    {/* Phase H4 FE-2 — Attendance is gated on at least
                    one section + one ACTIVE student + one class-
                    teacher assignment per the readiness aggregator.
                    The gate short-circuits to a configure-
                    prerequisites landing page when those gates fail
                    and falls through to the (Coming Soon) module
                    surface when ready=true. */}
                    <Route
                      path="/attendance"
                      element={
                        <RouteModuleGate module="attendance">
                          <PrerequisiteGate module="attendance">
                            <AttendancePage />
                          </PrerequisiteGate>
                        </RouteModuleGate>
                      }
                    />
                    {/* Phase G3 — Timetable workspace.
                    - `/timetable` is the list view (jobs + saved
                      timetables, KPI strip, row-level actions).
                    - `/timetable/:timetableId` is the weekly grid
                      editor — click a cell to open the manual-
                      override sheet, header carries publish/archive.
                    The submit-new-job constraint sheet lands in
                    G3 FE-1d. */}
                    {/* Phase H4 FE-2 — Timetable is gated on
                    subjects + sections + teacher_eligibility +
                    grade_demand per the catalogue readiness
                    aggregator. The gate short-circuits to the
                    configure-prerequisites landing on a freshly-
                    onboarded tenant; once everything is green it
                    passes through to the regular workspace. */}
                    {/* Phase F-finale-3 Slice 2 — `<RouteModuleGate>` is
                    the route-level defence layer for module-gated
                    surfaces. The sidebar already hides disabled-module
                    nav entries (Phase D0c), but deep-link traffic
                    bypasses that. The gate bounces to /dashboard with
                    a toast when the module is not enabled for the
                    tenant. It composes inside-out with
                    `<PrerequisiteGate>` (catalogue readiness). */}
                    <Route
                      path="/timetable"
                      element={
                        <RouteModuleGate module="timetable">
                          <PrerequisiteGate module="timetable">
                            <TimetablePage />
                          </PrerequisiteGate>
                        </RouteModuleGate>
                      }
                    />
                    {/* Phase G3 FE-4 — standalone, printable per-teacher
                    week schedule. Routed BEFORE the timetable detail
                    catch-all so the more-specific path matches first.
                    Shares `useTimetableDetail` with the editor and
                    renders only the slots assigned to `:teacherId`. */}
                    <Route
                      path="/timetable/:timetableId/teachers/:teacherId"
                      element={
                        <RouteModuleGate module="timetable">
                          <TeacherSchedulePage />
                        </RouteModuleGate>
                      }
                    />
                    <Route
                      path="/timetable/:timetableId"
                      element={
                        <RouteModuleGate module="timetable">
                          <TimetableDetailPage />
                        </RouteModuleGate>
                      }
                    />
                    {/* Phase G3.5 — Holidays & calendar.
                    IA admin authoring surface for tenant-wide and
                    branch-scoped holiday rows. The timetable solver
                    and attendance roll-call honour every entry.
                    Phase F-finale-3 Slice 2 — gated on `timetable`
                    module to match BE Slice 4 (calendar router
                    requires `timetable`). */}
                    <Route
                      path="/holidays"
                      element={
                        <RouteModuleGate module="timetable">
                          <HolidaysPage />
                        </RouteModuleGate>
                      }
                    />
                    {/* Phase H4 FE-1 — Tenant-wide academic catalogue.
                    Subjects + Sections CRUD plus a read-only
                    subject-coverage matrix. The timetable solver,
                    attendance, and substitution modules all read
                    from these collections; Phase H4 FE-2 will gate
                    those nav entries on the catalogue being
                    populated. Routes nest under `/catalogue` so the
                    sidebar item carries a single href and the
                    landing page deep-links into each sub-surface. */}
                    <Route path="/catalogue" element={<CataloguePage />} />
                    <Route path="/catalogue/subjects" element={<SubjectsCataloguePage />} />
                    <Route path="/catalogue/sections" element={<SectionsCataloguePage />} />
                    <Route
                      path="/catalogue/eligibility-matrix"
                      element={<EligibilityMatrixPage />}
                    />
                    {/* Phase H5 FE-1 — Assessments workspace.
                    IA authoring surface for assessment topics,
                    question bank (read-only inspector), and
                    templates. Templates are the unit bound to an
                    admission-stage `template_slug` requirement.
                    Routes nest under `/assessments` so the sidebar
                    carries a single href and the landing page deep-
                    links into each sub-surface. Sidebar visibility
                    is gated on the `admissions` module (the only
                    consumer of templates today).
                    Phase F-finale-3 Slice 2 — wrap each route in
                    `<RouteModuleGate module="admissions">` so direct
                    URL hits also respect the gate (sidebar gating
                    alone is bypassable). */}
                    <Route
                      path="/assessments"
                      element={
                        <RouteModuleGate module="admissions">
                          <RouteVerticalConceptGate concept="admissionAssessments">
                            <AssessmentsPage />
                          </RouteVerticalConceptGate>
                        </RouteModuleGate>
                      }
                    />
                    <Route
                      path="/assessments/topics"
                      element={
                        <RouteModuleGate module="admissions">
                          <RouteVerticalConceptGate concept="admissionAssessments">
                            <AssessmentTopicsPage />
                          </RouteVerticalConceptGate>
                        </RouteModuleGate>
                      }
                    />
                    <Route
                      path="/assessments/question-bank"
                      element={
                        <RouteModuleGate module="admissions">
                          <RouteVerticalConceptGate concept="admissionAssessments">
                            <AssessmentQuestionBankPage />
                          </RouteVerticalConceptGate>
                        </RouteModuleGate>
                      }
                    />
                    <Route
                      path="/assessments/templates"
                      element={
                        <RouteModuleGate module="admissions">
                          <RouteVerticalConceptGate concept="admissionAssessments">
                            <AssessmentTemplatesPage />
                          </RouteVerticalConceptGate>
                        </RouteModuleGate>
                      }
                    />
                    <Route
                      path="/assessments/templates/:slug"
                      element={
                        <RouteModuleGate module="admissions">
                          <RouteVerticalConceptGate concept="admissionAssessments">
                            <AssessmentTemplateDetailPage />
                          </RouteVerticalConceptGate>
                        </RouteModuleGate>
                      }
                    />
                    <Route path="/exams" element={<ModuleComingSoonPage />} />
                    <Route
                      path="/communications"
                      element={
                        <RouteModuleGate module="communications">
                          <CommunicationsPage />
                        </RouteModuleGate>
                      }
                    />
                    <Route
                      path="/reports"
                      element={
                        <RouteModuleGate module="reports">
                          <ReportsPage />
                        </RouteModuleGate>
                      }
                    />
                    <Route path="/transport" element={<ModuleComingSoonPage />} />
                    <Route path="/hostel" element={<ModuleComingSoonPage />} />
                    <Route path="/library" element={<ModuleComingSoonPage />} />
                    <Route
                      path="/learning"
                      element={
                        <RouteModuleGate module="learning">
                          <RouteVerticalConceptGate concept="learnerPortal">
                            <LearningPage />
                          </RouteVerticalConceptGate>
                        </RouteModuleGate>
                      }
                    />
                    <Route
                      path="/helpdesk"
                      element={
                        <RouteModuleGate module="helpdesk">
                          <HelpdeskPage />
                        </RouteModuleGate>
                      }
                    />
                    {/* CRM is the admissions pipeline; old links land there. */}
                    <Route path="/crm" element={<Navigate to="/admissions" replace />} />
                    {/* Phase SC-2b — owner finances. Expense ledger + unified
                    staff-cost ledger + monthly gross/net P&L. Reads gate on
                    view_accounting, writes on manage_accounting (admin-
                    ceiling); the RouteModuleGate bounces deep-link traffic
                    when the `accounting` module is disabled for the tenant,
                    mirroring the BE `require_module('accounting')`. */}
                    <Route
                      path="/accounting"
                      element={
                        <RouteModuleGate module="accounting">
                          <AccountingPage />
                        </RouteModuleGate>
                      }
                    />
                    {/* Phase SC-22d — Team pay: attendance, pay, advances,
                    leave and the SC-2c pay runs (now the "Pay runs" tab).
                    The RouteModuleGate bounces deep-link traffic when the
                    `payroll` module is disabled, mirroring the BE
                    `require_module('payroll')`. `/payroll` stays as a
                    redirect so old bookmarks land on the runs tab. */}
                    <Route
                      path="/team-pay"
                      element={
                        <RouteModuleGate module="payroll">
                          <TeamPayPage />
                        </RouteModuleGate>
                      }
                    />
                    <Route path="/payroll" element={<Navigate to="/team-pay?tab=runs" replace />} />
                    <Route
                      path="/audit-export"
                      element={
                        <RouteModuleGate module="audit_export">
                          <AuditExportPage />
                        </RouteModuleGate>
                      }
                    />
                    <Route
                      path="/dpdp-consent"
                      element={
                        <RouteModuleGate module="dpdp_consent">
                          <DpdpConsentPage />
                        </RouteModuleGate>
                      }
                    />
                    <Route path="/parent-portal" element={<ModuleComingSoonPage />} />
                    {/* Phase E2c — Settings + Security routes finally wired
                    (the sidebar entries shipped in Phase D0c, the routes
                    didn't, so the catch-all `*` was silently bouncing
                    these to /dashboard). The pages today are tenant-aware
                    landing surfaces that name the sub-features and the
                    phase each lands in — refusing to dead-end the user
                    into a placeholder per Iron Rule #2. */}
                    <Route path="/settings" element={<SettingsPage />} />
                    {/* Phase F2b — IA self-serve branding. Logo + favicon +
                    primary/secondary colours applied to the admin shell
                    (live via TenantBrandingProvider) and served to the
                    learners' portal. Writes hit /me/branding (JWT-scoped,
                    no slug in the URL). */}
                    <Route path="/settings/branding" element={<SettingsBrandingPage />} />
                    {/* Phase F1 — RBAC overrides FE. Sub-route under
                    /settings so the breadcrumb is honest about where
                    the editor lives. The Members & roles card on
                    SettingsPage links here directly. */}
                    <Route path="/settings/designations" element={<DesignationsPage />} />
                    {/* Phase F0b — Notification triggers FE. SA-side CRUD
                    surface for the action-gated stage engine's event →
                    template → recipients mapping. BE owns the
                    `manage_notifications` enforcement; the page hides
                    write affordances for users without it. */}
                    <Route
                      path="/settings/notifications"
                      element={<NotificationTriggersSettingsRoute />}
                    />
                    {/* Phase H2 — Apply portal lifecycle. IA self-serve
                    editor for `Tenant.apply_portal` (active, schedule
                    kind, scheduled windows, per-grade filtering, OTP,
                    closed-state message). The public apply portal
                    enforces the same config server-side via
                    `_ensure_apply_portal_open`. */}
                    <Route
                      path="/settings/admissions-lifecycle"
                      element={<AdmissionsLifecyclePage />}
                    />
                    <Route path="/settings/learner-app" element={<LearnerAppSettingsPage />} />
                    <Route path="/settings/learner-form" element={<LearnerFormSettingsPage />} />
                    <Route path="/settings/team-form" element={<TeamFormSettingsPage />} />
                    <Route
                      path="/settings/learning-defaults"
                      element={<LearningDefaultsSettingsPage />}
                    />
                    {/* Phase F-finale-5 Slice 5a — tenant-self Forms editor.
                    `/forms` is the list (DataTable, IR #16); `/forms/:slug`
                    is the FormBuilder editor mirroring the SA surface. Both
                    pages resolve `useCanEditForms` internally and degrade to
                    a read-only / paused view when the SA has switched off
                    `forms_editable_by_tenant`, so deep-link traffic that
                    bypasses the gated sidebar entry still lands on a honest,
                    non-dead-end surface (Iron Rule #2). */}
                    <Route path="/forms" element={<FormsListPage />} />
                    <Route path={FORM_EDITOR_ROUTE_PATTERN} element={<IaFormEditorPage />} />
                    <Route path="/security" element={<SecurityPage />} />
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  </Route>
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </SupportSessionGate>
            </TenantBrandingProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
      {/* Phase E1.b.1 — Single mount point for every toast across the IA.
        Sits inside AppearanceProvider so the toast surface reads the
        active (light | dark) mode + palette tokens, but OUTSIDE the
        router so navigation never tears down in-flight toasts. */}
      <Toaster position="bottom-right" richColors closeButton />
    </AppearanceProvider>
  );
}
